import React, { FunctionComponent, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import './styles/ChipsNavigation.scss';

import { Badge, BadgeProps, Stack, StackProps } from '@mui/material';

import { IMenuItem } from '@dw/models/shared';
import { classNames, componentClassName } from '@dw/utils';

import { Chip } from './Chip';
import { ArrowBack, ArrowForward } from '@mui/icons-material';

interface IChipsNavItem extends IMenuItem {
  badgeValue?: ReactNode;
  badgeColor?: BadgeProps['color'];
}

interface IChipsNavigationProps extends StackProps {
  options: IChipsNavItem[];
  activeItem: string;
  optionSelected: (option: IChipsNavItem) => void;
}
export const ChipsNavigation: FunctionComponent<IChipsNavigationProps> = (props: IChipsNavigationProps) => {

  const {
    options,
    activeItem,
    optionSelected,
    ...stackProps
  } = props;

  const scrollWrapper = useRef<HTMLDivElement>(null);
  const visibleOptions = useMemo(() => options.filter(x => !x.hidden), [options]);

  const [overflows, setOverflows] = useState({
    left: false,
    right: false
  });

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollWrapper.current;
    if (!el) {
      return;
    }

    const sortedChildren = Array.from(el.children)
      .map(x => ({
        rightOfElement: (x as HTMLElement).offsetLeft + (x as HTMLElement).clientWidth,
        leftOfElement: (x as HTMLElement).offsetLeft
      }))
      .sort((a, b) => a.leftOfElement - b.leftOfElement);

    if (direction === 'right') {
      const visibleRight = el.scrollLeft + el.clientWidth;
      const firstChildNotFullyVisible = sortedChildren
        .find(x => x.rightOfElement > visibleRight);

      if (!firstChildNotFullyVisible) {
        return;
      }

      el.scrollTo({
        left: (firstChildNotFullyVisible.rightOfElement - el.clientWidth) + 35,
        behavior: 'smooth'
      });
    } else if (direction === 'left') {
      const lastChildNotFullyVisible = sortedChildren
        .filter(x => x.leftOfElement < el.scrollLeft)
        .at(-1);

      if (!lastChildNotFullyVisible) {
        return;
      }

      el.scrollTo({
        left: Math.max(0, lastChildNotFullyVisible.leftOfElement - 35),
        behavior: 'smooth'
      });
    }
  }

  const checkOverflows = () => {
    if (!scrollWrapper.current) {
      setOverflows({ left: false, right: false });
      return;
    }

    const el = scrollWrapper.current;
    setOverflows({
      left: el.scrollLeft > 0,
      right: el.scrollWidth - el.scrollLeft > el.clientWidth
    });
  }

  useEffect(() => {
    if (!scrollWrapper.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => checkOverflows());
    resizeObserver.observe(scrollWrapper.current);

    const scrollWrapperEl = scrollWrapper.current;
    scrollWrapperEl.addEventListener('scroll', () => checkOverflows());

    return () => {
      resizeObserver.disconnect();
      scrollWrapperEl.removeEventListener('scroll', () => checkOverflows());
    }
  }, []);

  const cn = componentClassName('chips-navigation');

  return (
    <Stack
      direction='row'
      alignItems='center'
      columnGap={2}
      className={cn()}
      {...stackProps}
    >
      
      {overflows.left && (
        <div
          className={classNames(cn('scroller'), cn('scroller', 'left'))}
          onClick={() => scroll('left')}
        >
          <ArrowBack color='textDisabled' />
        </div>
      )}

      <Stack
        direction='row'
        columnGap={2}
        ref={scrollWrapper}
        flex={1}
        className={classNames(cn('scroll-wrapper'), {
          [cn('scroll-wrapper', 'left-scroll-padding')]: overflows.left,
          [cn('scroll-wrapper', 'right-scroll-padding')]: overflows.right
        })}
      >

        {visibleOptions.map((x, index) => (
          <ChipsNavItem
            key={x.value ?? index}
            item={x}
            isActive={activeItem === (x.value ?? x.text)}
            onSelected={() => optionSelected(x)}
          />
        ))}
      </Stack>

      {overflows.right && (
        <div
          className={classNames(cn('scroller'), cn('scroller', 'right'))}
          onClick={() => scroll('right')}
        >
          <ArrowForward color='textDisabled' />
        </div>
      )}
    </Stack>
  )
}

interface IChipsNavItemProps {
  item: IChipsNavItem;
  isActive: boolean;
  onSelected: () => void;
}
const ChipsNavItem: FunctionComponent<IChipsNavItemProps> = (props: IChipsNavItemProps) => {

  const {
    item,
    isActive,
    onSelected
  } = props;

  const cn = componentClassName('chips-navigation-chip');

  const chip = (
    <Chip
      onClick={() => onSelected()}
      icon={item.icon as React.ReactElement}
      label={item.text}
      color={isActive ? 'primary' : undefined}
      className={cn()}
    />
  );

  if (item.badgeValue) {
    return (
      <Badge badgeContent={item.badgeValue} className={cn('badge-wrapper')} color={item.badgeColor ?? 'warning'} slotProps={{
        badge: {
          className: cn('badge-wrapper', 'badge')
        }
      }}>
        {chip}
      </Badge>
    )
  }

  return chip;
}